import createDonutChartOptions, { ChartDatum } from 'lib/agCharts/donutChart';
import { useGetAllObnlNamesQuery } from 'lib/graphql/graphql';
import { mergePaginatedList, usePagination } from 'lib/graphql/pagination';
import LanguageContext from 'providers/i18n/LanguageContext';
import { useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ChartCard from '../ChartCard/ChartCard';
import useObnlDataQuery from './useObnlDataQuery';

interface ObnlsBreakdownChartProps {
  selectedTime: string
  selectedWaitingLine: string
}

function ObnlsBreakdownChart(props: ObnlsBreakdownChartProps) {
  const { selectedTime, selectedWaitingLine } = props;

  const { language } = useContext(LanguageContext);

  const { formatMessage } = useIntl();

  const {
    data: obnlData,
    loading: obnlLoading,
    hasNextPage: obnlHasNextPage,
  } = usePagination(
    useGetAllObnlNamesQuery(),
    (d) => d.getAllObnls.lastEvaluated,
    (existing, incoming) => ({
      getAllObnls: mergePaginatedList(
        existing.getAllObnls,
        incoming.getAllObnls,
        'obnls',
      ),
    }),
  );
  const obnls = obnlData?.getAllObnls.obnls;
  const fetchingObnls = obnlLoading || obnlHasNextPage;

  const [
    aggregates,
    {
      loading: aggregatesLoading,
      hasNextPage: aggregatesHasNextPage,
    },
  ] = useObnlDataQuery(selectedTime, selectedWaitingLine);
  const fetchingAggregates = aggregatesLoading || aggregatesHasNextPage;

  const obnlNamesMap = useMemo(() => (
    obnls?.reduce(
      (acc, obnl) => ({ ...acc, [obnl.id]: obnl.name }),
      {} as Record<string, string>,
    ) || {}
  ), [obnls]);

  const chartOptions = useMemo(() => {
    if (aggregates) {
      const data: Record<string, number> = {};
      aggregates.forEach(({ transactionPerObnl }) => {
        Object.keys(transactionPerObnl).forEach((obnlId) => {
          if (data[obnlId]) {
            data[obnlId] += transactionPerObnl[obnlId];
          } else {
            data[obnlId] = transactionPerObnl[obnlId];
          }
        });
      });
      const chartData = Object.keys(data).map<ChartDatum>((obnlId) => ({
        asset: obnlNamesMap[obnlId] || obnlId,
        amount: data[obnlId],
      }));
      return createDonutChartOptions(chartData, language, formatMessage({ id: 'transactions' }));
    }
    return {};
  }, [aggregates, formatMessage, language, obnlNamesMap]);

  return (
    <ChartCard
      title={<FormattedMessage id="obnls_breakdown" />}
      loading={fetchingAggregates || fetchingObnls}
      chartOptions={chartOptions}
    />
  );
}

export default ObnlsBreakdownChart;
